import React, { useState, useRef, useCallback } from 'react';
import { Plus, Image as ImageIcon } from 'lucide-react';
import { toPxRect, toNormRect, clampNormRect } from '../../lib/coords';
import { applySnapping, snapToGrid } from '../../lib/snapping';
import { useEditorStore } from '../../store/editorStore';

// Signer colors for visual distinction (must match DocumentFieldEditorV2.js)
const SIGNER_COLORS = [
  { bg: '#3B82F6', border: '#2563EB', text: '#1E40AF', light: '#DBEAFE' }, // Blue
  { bg: '#22C55E', border: '#16A34A', text: '#166534', light: '#DCFCE7' }, // Green
  { bg: '#A855F7', border: '#9333EA', text: '#6B21A8', light: '#F3E8FF' }, // Purple
  { bg: '#F97316', border: '#EA580C', text: '#9A3412', light: '#FFEDD5' }, // Orange
  { bg: '#EC4899', border: '#DB2777', text: '#9D174D', light: '#FCE7F3' }, // Pink
];

const FIELD_COLORS = {
  signature: '#9333ea',
  initials: '#7c3aed',
  name: '#3b82f6',
  email: '#06b6d4',
  text: '#eab308',
  date: '#ec4899',
  checkbox: '#22c55e',
  radio: '#8b5cf6',
  image: '#f59e0b',
};

// Minimum sizes in pixels
const MIN_SIZES = {
  signature: { width: 80, height: 30 },
  initials: { width: 60, height: 30 },
  name: { width: 120, height: 25 },
  email: { width: 150, height: 25 },
  text: { width: 80, height: 25 },
  date: { width: 100, height: 25 },
  checkbox: { width: 20, height: 20 },
  radio: { width: 24, height: 24 },  // Just fit one circle (16px + 8px padding)
  image: { width: 80, height: 80 },
};

const FieldNode = ({ field, pageMetrics, scale, isSelected }) => {
  const { updateField, updateFieldWithHistory, selectField, fields, signers } = useEditorStore();

  const [dragState, setDragState] = useState(null);
  const [snapGuides, setSnapGuides] = useState([]);

  // Convert normalized rect to pixel rect
  const pxRect = toPxRect(
    { x: field.x, y: field.y, w: field.w, h: field.h },
    pageMetrics,
    scale
  );

  const color = FIELD_COLORS[field.type] || '#6b7280';

  // Determine signer assignment for visual indicators
  const assignedTo = field.props?.assignedTo;
  const hasMultipleSigners = signers && signers.length > 1;
  // Match by signerId (e.g., "signer_1") instead of email
  const signerIndex = hasMultipleSigners && assignedTo
    ? signers.findIndex(s => s.signerId === assignedTo)
    : -1;
  const signerColor = signerIndex >= 0 ? SIGNER_COLORS[signerIndex % SIGNER_COLORS.length] : null;
  const signerLabel = signerIndex >= 0 ? `S${signerIndex + 1}` : null;
  
  // Very flexible min size for radio and checkbox groups - allow tiny containers
  let minSize = MIN_SIZES[field.type] || MIN_SIZES.text;
  if (field.type === 'radio' || field.type === 'checkbox-group') {
    // Minimal constraint - just fit one circle/square (like Signeasy)
    minSize = { width: 24, height: 24 };
  }
  
  // Check if checkbox (maintains square aspect ratio)
  const isCheckbox = field.type === 'checkbox';
  
  // Mouse down - start drag or resize
  const handleMouseDown = useCallback((e, handle = null) => {
    if (e.button !== 0) return; // Only left click
    e.stopPropagation();
    e.preventDefault();
    
    console.log('handleMouseDown called', { handle, fieldId: field.id, fieldType: field.type });
    
    selectField(field.id);
    
    setDragState({
      type: handle ? 'resize' : 'move',
      handle,
      startX: e.clientX,
      startY: e.clientY,
      originalX: field.x,
      originalY: field.y,
      originalW: field.w,
      originalH: field.h,
      moved: false,
    });
  }, [field.id, field.x, field.y, field.w, field.h, selectField]);
  
  // Mouse move - dragging or resizing
  const handleMouseMove = useCallback((e) => {
    if (!dragState) return;
    
    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;
    
    // Check if moved more than 3px (activation threshold)
    const hasMoved = dragState.moved || (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3);
    
    if (!dragState.moved && hasMoved) {
      console.log('Drag activated', { type: dragState.type, handle: dragState.handle });
      setDragState(prev => ({ ...prev, moved: true }));
    }
    
    if (!hasMoved) return;
    
    if (dragState.type === 'move') {
      // Convert delta to normalized
      const deltaNormX = deltaX / (pageMetrics.width * scale);
      const deltaNormY = deltaY / (pageMetrics.height * scale);
      
      let newX = dragState.originalX + deltaNormX;
      let newY = dragState.originalY + deltaNormY;
      
      // Create candidate field for snapping
      const candidate = {
        ...field,
        x: newX,
        y: newY,
      };
      
      // Apply snapping
      const snapped = applySnapping(candidate, fields, pageMetrics, scale);
      
      // Check if field should move to another page (cross-page dragging)
      let targetPage = field.page;
      let finalX = snapped.x;
      let finalY = snapped.y;
      
      // If dragged below current page (y > 1.0), move to next page
      if (snapped.y > 1.0) {
        targetPage = field.page + 1;
        finalY = snapped.y - 1.0; // Offset from top of next page
      }
      // If dragged above current page (y + h < 0), move to previous page
      else if (snapped.y + field.h < 0) {
        targetPage = Math.max(1, field.page - 1);
        finalY = 1.0 + snapped.y + field.h; // Position near bottom of previous page
      }
      
      // Clamp to bounds only after determining the page
      const clamped = clampNormRect({
        x: finalX,
        y: finalY,
        w: field.w,
        h: field.h,
      });
      
      // Update field position (without history during drag)
      updateField(field.id, {
        x: clamped.x,
        y: clamped.y,
        ...(targetPage !== field.page && { page: targetPage })
      });
      
      // Update snap guides
      setSnapGuides(snapped.guides || []);
    } else if (dragState.type === 'resize') {
      // Resize logic
      const deltaNormX = deltaX / (pageMetrics.width * scale);
      const deltaNormY = deltaY / (pageMetrics.height * scale);
      
      let newW = dragState.originalW;
      let newH = dragState.originalH;
      let newX = dragState.originalX;
      let newY = dragState.originalY;
      
      // Calculate based on handle (8 directions: 4 corners + 4 edges)
      switch (dragState.handle) {
        // Corner handles
        case 'se': // Southeast (bottom-right)
          newW = dragState.originalW + deltaNormX;
          newH = dragState.originalH + deltaNormY;
          break;
        case 'sw': // Southwest (bottom-left)
          newW = dragState.originalW - deltaNormX;
          newH = dragState.originalH + deltaNormY;
          newX = dragState.originalX + deltaNormX;
          break;
        case 'ne': // Northeast (top-right)
          newW = dragState.originalW + deltaNormX;
          newH = dragState.originalH - deltaNormY;
          newY = dragState.originalY + deltaNormY;
          break;
        case 'nw': // Northwest (top-left)
          newW = dragState.originalW - deltaNormX;
          newH = dragState.originalH - deltaNormY;
          newX = dragState.originalX + deltaNormX;
          newY = dragState.originalY + deltaNormY;
          break;
        
        // Edge handles
        case 'n': // North (top)
          newH = dragState.originalH - deltaNormY;
          newY = dragState.originalY + deltaNormY;
          break;
        case 's': // South (bottom)
          newH = dragState.originalH + deltaNormY;
          break;
        case 'e': // East (right)
          newW = dragState.originalW + deltaNormX;
          break;
        case 'w': // West (left)
          newW = dragState.originalW - deltaNormX;
          newX = dragState.originalX + deltaNormX;
          break;
      }
      
      // For checkbox, maintain square aspect ratio
      if (isCheckbox) {
        const avgSize = (newW + newH) / 2;
        newW = avgSize;
        newH = avgSize;
        
        // Adjust position for left/top handles to keep opposite corner fixed
        if (dragState.handle.includes('w') || dragState.handle === 'w') {
          newX = dragState.originalX + dragState.originalW - avgSize;
        }
        if (dragState.handle.includes('n') || dragState.handle === 'n') {
          newY = dragState.originalY + dragState.originalH - avgSize;
        }
      }
      
      // Apply minimum size constraints
      const minW = minSize.width / (pageMetrics.width * scale);
      const minH = minSize.height / (pageMetrics.height * scale);
      
      if (newW < minW) {
        newW = minW;
        if (dragState.handle.includes('w') || dragState.handle === 'w') {
          newX = dragState.originalX + dragState.originalW - minW;
        }
      }
      
      if (newH < minH) {
        newH = minH;
        if (dragState.handle.includes('n') || dragState.handle === 'n') {
          newY = dragState.originalY + dragState.originalH - minH;
        }
      }
      
      // Snap dimensions to grid
      newW = snapToGrid(newW, pageMetrics.width, scale);
      newH = snapToGrid(newH, pageMetrics.height, scale);
      
      // Clamp to bounds
      const clamped = clampNormRect({
        x: newX,
        y: newY,
        w: newW,
        h: newH,
      });
      
      // Update field
      updateField(field.id, {
        x: clamped.x,
        y: clamped.y,
        w: clamped.w,
        h: clamped.h,
      });
    }
  }, [dragState, field, pageMetrics, scale, fields, updateField, minSize]);
  
  // Mouse up - finish drag or resize
  const handleMouseUp = useCallback(() => {
    if (dragState && dragState.moved) {
      // Save to history
      const updates = {
        updatedAt: new Date().toISOString(),
      };
      
      if (dragState.type === 'move') {
        updates.x = field.x;
        updates.y = field.y;
        updates.page = field.page; // Save page changes
      } else if (dragState.type === 'resize') {
        updates.x = field.x;
        updates.y = field.y;
        updates.w = field.w;
        updates.h = field.h;
      }
      
      updateFieldWithHistory(field.id, updates);
    }
    
    setDragState(null);
    setSnapGuides([]);
  }, [dragState, field, updateFieldWithHistory]);
  
  // Attach global listeners when dragging
  React.useEffect(() => {
    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragState, handleMouseMove, handleMouseUp]);
  
  const isDragging = dragState && dragState.moved && dragState.type === 'move';
  const isResizing = dragState && dragState.moved && dragState.type === 'resize';
  
  // Check if field type should be resizable.
  // Checkbox stays fixed at its default size (not user-resizable).
  const isResizable = field.type !== 'checkbox';
  
  // Get cursor based on current state
  const getCursor = () => {
    if (isDragging) return 'grabbing';
    if (isResizing) {
      const handle = dragState.handle;
      if (handle === 'n' || handle === 's') return 'ns-resize';
      if (handle === 'e' || handle === 'w') return 'ew-resize';
      if (handle === 'ne' || handle === 'sw') return 'nesw-resize';
      if (handle === 'nw' || handle === 'se') return 'nwse-resize';
    }
    return 'grab';
  };
  
  // Add option handler (works for radio and checkbox-group, not single checkbox)
  const handleAddOption = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (field.type !== 'radio' && field.type !== 'checkbox-group') return;
    
    const currentOptions = field.props?.options || [];
    const maxOptions = field.props?.maxOptions || 50;
    
    // Check if at max
    if (currentOptions.length >= maxOptions) {
      return;
    }
    
    // Detect current layout from existing options
    let detectedLayout = field.props?.layout || 'vertical';
    let newX = 0.05;  // Default x position (normalized)
    let newY = 0.3;   // Default y position (normalized)
    
    if (currentOptions.length >= 1) {
      const lastOption = currentOptions[currentOptions.length - 1];
      
      if (detectedLayout === 'horizontal') {
        // Add to the right
        newX = (lastOption.x || 0.05) + 0.15;  // Offset right
        newY = lastOption.y || 0.3;
        
        // Wrap to next row if too far right
        if (newX > 0.85) {
          newX = 0.05;
          newY = (lastOption.y || 0.3) + 0.15;
        }
      } else {
        // Add below
        newX = lastOption.x || 0.05;
        newY = (lastOption.y || 0.3) + 0.1;  // Offset down
        
        // Wrap to next column if too far down
        if (newY > 0.85) {
          newY = 0.3;
          newX = (lastOption.x || 0.05) + 0.15;
        }
      }
    }
    
    // Generate new option
    const n = currentOptions.length + 1;
    const labelPattern = field.props?.defaultLabelPattern || 'Option ${n}';
    const valuePattern = field.props?.defaultValuePattern || 'opt_${n}';
    
    let label = labelPattern.replace('${n}', n.toString());
    let value = valuePattern.replace('${n}', n.toString());
    
    // Ensure unique value
    while (currentOptions.some(opt => opt.value === value)) {
      const randomSuffix = Math.random().toString(36).substring(2, 6);
      value = `${valuePattern.replace('${n}', n.toString())}_${randomSuffix}`;
    }
    
    const newOption = {
      id: Math.random().toString(36).substring(2, 9),
      label,
      value,
      x: newX,
      y: newY
    };
    
    const newOptions = [...currentOptions, newOption];
    
    // Update field with new options
    updateFieldWithHistory(field.id, {
      props: {
        ...field.props,
        options: newOptions
      }
    });
  }, [field, updateFieldWithHistory]);
  
  return (
    <>
      {/* Snap guides */}
      {snapGuides.map((guide, i) => (
        <line
          key={i}
          x1={guide.type === 'vertical' ? guide.x * pageMetrics.width * scale : 0}
          y1={guide.type === 'horizontal' ? guide.y * pageMetrics.height * scale : 0}
          x2={guide.type === 'vertical' ? guide.x * pageMetrics.width * scale : pageMetrics.width * scale}
          y2={guide.type === 'horizontal' ? guide.y * pageMetrics.height * scale : pageMetrics.height * scale}
          stroke="#3b82f6"
          strokeWidth={1}
          strokeDasharray="4,4"
          pointerEvents="none"
        />
      ))}
      
      {/* Field */}
      <g
        className="field-node pointer-events-auto"
        transform={`translate(${pxRect.left}, ${pxRect.top})`}
        style={{
          cursor: getCursor(),
          zIndex: field.z || 0,
        }}
      >
        {/* Field background */}
        <rect
          width={pxRect.width}
          height={pxRect.height}
          fill={(field.type === 'radio' || field.type === 'checkbox-group') ? 'white' : (signerColor ? signerColor.light : color)}
          fillOpacity={(field.type === 'radio' || field.type === 'checkbox-group') ? 0.1 : ((isDragging || isResizing) ? 0.5 : 0.4)}
          stroke={signerColor ? signerColor.border : color}
          strokeWidth={isSelected ? 3 : (signerColor ? 2 : 1)}
          strokeDasharray={(isDragging || isResizing) ? '5,5' : 'none'}
          rx={2}
          onMouseDown={(e) => handleMouseDown(e, null)}
        />

        {/* Signer badge - only show when multiple signers and field is assigned */}
        {hasMultipleSigners && signerColor && signerLabel && (
          <g pointerEvents="none">
            {/* Badge background */}
            <rect
              x={pxRect.width - 22}
              y={-8}
              width={24}
              height={16}
              fill={signerColor.bg}
              rx={3}
            />
            {/* Badge text */}
            <text
              x={pxRect.width - 10}
              y={4}
              textAnchor="middle"
              fontSize={10}
              fontWeight="bold"
              fill="white"
            >
              {signerLabel}
            </text>
          </g>
        )}
        
        {/* Radio Group specific rendering */}
        {field.type === 'radio' && (() => {
          const options = field.props?.options || [];
          const radioSize = 14;  // Circle diameter
          const optionHeight = 24;
          const labelMaxWidth = pxRect.width - 40;
          
          return (
            <g pointerEvents="none">
              {/* Radio options - SignEasy style: just circles, no container label */}
              {options.map((option, index) => {
                // ALWAYS use stored position (ratios 0-1 within container)
                // If missing, use vertical layout defaults (consistent with SigningSession.js)
                const defaultX = 0.1;  // 10% from left
                const defaultY = 0.15 + (index * 0.35);  // Stacked vertically
                const optionX = (option.x !== undefined && option.x !== null ? option.x : defaultX) * pxRect.width;
                const optionY = (option.y !== undefined && option.y !== null ? option.y : defaultY) * pxRect.height;
                
                const isDefault = field.props?.defaultValue === option.value;
                
                return (
                  <g 
                    key={option.id}
                    style={{ cursor: 'grab', pointerEvents: 'auto' }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      
                      // Store option drag state
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startOptionX = optionX;
                      const startOptionY = optionY;
                      let latestOptions = [...options];
                      
                      const handleOptionDrag = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                        
                        // Calculate new position with minimal constraints
                        const newX = Math.max(radioSize/2, Math.min(pxRect.width - radioSize/2, startOptionX + deltaX));
                        const newY = Math.max(radioSize/2, Math.min(pxRect.height - radioSize/2, startOptionY + deltaY));
                        
                        // Store EXACT position as ratio (SignEasy style - no snapping)
                        const normalizedX = newX / pxRect.width;
                        const normalizedY = newY / pxRect.height;
                        
                        latestOptions = latestOptions.map(opt => 
                          opt.id === option.id 
                            ? { ...opt, x: normalizedX, y: normalizedY }
                            : opt
                        );
                        
                        updateField(field.id, {
                          props: {
                            ...field.props,
                            options: latestOptions
                          }
                        });
                      };
                      
                      const handleOptionDragEnd = () => {
                        window.removeEventListener('mousemove', handleOptionDrag);
                        window.removeEventListener('mouseup', handleOptionDragEnd);
                        
                        // Save final position with history
                        const finalOptions = latestOptions;
                        
                        if (finalOptions.length >= 2) {
                          const positions = finalOptions.map(opt => ({
                            x: (opt.x || 0) * pxRect.width,
                            y: (opt.y || 0) * pxRect.height
                          }));
                          
                          // Calculate average vertical vs horizontal spread
                          const ySpread = Math.max(...positions.map(p => p.y)) - Math.min(...positions.map(p => p.y));
                          const xSpread = Math.max(...positions.map(p => p.x)) - Math.min(...positions.map(p => p.x));
                          
                          const detectedLayout = xSpread > ySpread ? 'horizontal' : 'vertical';
                          
                          // Save both options positions AND layout with history
                          updateFieldWithHistory(field.id, {
                            props: {
                              ...field.props,
                              options: finalOptions,
                              layout: detectedLayout
                            }
                          });
                        } else {
                          // Just save options positions even if only 1 option
                          updateFieldWithHistory(field.id, {
                            props: {
                              ...field.props,
                              options: finalOptions
                            }
                          });
                        }
                      };
                      
                      window.addEventListener('mousemove', handleOptionDrag);
                      window.addEventListener('mouseup', handleOptionDragEnd);
                    }}
                  >
                    {/* Radio circle */}
                    <circle
                      cx={optionX + radioSize / 2}
                      cy={optionY + radioSize / 2}
                      r={radioSize / 2}
                      fill="white"
                      stroke="#9ca3af"
                      strokeWidth="1.5"
                      style={{ pointerEvents: 'auto' }}
                    />
                    {isDefault && (
                      <circle
                        cx={optionX + radioSize / 2}
                        cy={optionY + radioSize / 2}
                        r={radioSize / 4}
                        fill={color}
                      />
                    )}
                    
                    {/* Option label - ONLY show if label exists and is not empty */}
                    {option.label && option.label.trim() && (
                      <foreignObject
                        x={optionX + radioSize + 6}
                        y={optionY - 2}
                        width={Math.max(0, labelMaxWidth - radioSize - 6)}
                        height={optionHeight}
                        style={{ pointerEvents: 'auto' }}
                      >
                        <div
                          xmlns="http://www.w3.org/1999/xhtml"
                          style={{
                            fontSize: '10px',
                            color: '#4b5563',
                            lineHeight: '1.4',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            cursor: 'grab'
                          }}
                        >
                          {option.label}
                        </div>
                      </foreignObject>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })()}
        
        {/* Checkbox Group specific rendering (only for checkbox-group, not single checkbox) */}
        {field.type === 'checkbox-group' && (() => {
          const options = field.props?.options || [];
          const checkboxSize = 14;  // Square size
          const optionHeight = 24;
          const labelMaxWidth = pxRect.width - 40;
          
          return (
            <g pointerEvents="none">
              {/* Checkbox options - SignEasy style: just squares, no container label */}
              {options.map((option, index) => {
                // ALWAYS use stored position (ratios 0-1 within container)
                // If missing, use vertical layout defaults (consistent with SigningSession.js)
                const defaultX = 0.1;  // 10% from left
                const defaultY = 0.15 + (index * 0.35);  // Stacked vertically
                const optionX = (option.x !== undefined && option.x !== null ? option.x : defaultX) * pxRect.width;
                const optionY = (option.y !== undefined && option.y !== null ? option.y : defaultY) * pxRect.height;
                
                return (
                  <g 
                    key={option.id}
                    style={{ cursor: 'grab', pointerEvents: 'auto' }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      
                      // Store option drag state
                      const startX = e.clientX;
                      const startY = e.clientY;
                      const startOptionX = optionX;
                      const startOptionY = optionY;
                      let latestOptions = [...options];
                      
                      const handleOptionDrag = (moveEvent) => {
                        const deltaX = moveEvent.clientX - startX;
                        const deltaY = moveEvent.clientY - startY;
                        
                        // Calculate new position with minimal constraints
                        const newX = Math.max(checkboxSize/2, Math.min(pxRect.width - checkboxSize/2, startOptionX + deltaX));
                        const newY = Math.max(checkboxSize/2, Math.min(pxRect.height - checkboxSize/2, startOptionY + deltaY));
                        
                        // Store EXACT position as ratio (SignEasy style - no snapping)
                        const normalizedX = newX / pxRect.width;
                        const normalizedY = newY / pxRect.height;
                        
                        latestOptions = latestOptions.map(opt => 
                          opt.id === option.id 
                            ? { ...opt, x: normalizedX, y: normalizedY }
                            : opt
                        );
                        
                        updateField(field.id, {
                          props: {
                            ...field.props,
                            options: latestOptions
                          }
                        });
                      };
                      
                      const handleOptionDragEnd = () => {
                        window.removeEventListener('mousemove', handleOptionDrag);
                        window.removeEventListener('mouseup', handleOptionDragEnd);
                        
                        // Save final position with history
                        const finalOptions = latestOptions;
                        
                        if (finalOptions.length >= 2) {
                          const positions = finalOptions.map(opt => ({
                            x: (opt.x || 0) * pxRect.width,
                            y: (opt.y || 0) * pxRect.height
                          }));
                          
                          // Detect layout from positions
                          const ySpread = Math.max(...positions.map(p => p.y)) - Math.min(...positions.map(p => p.y));
                          const xSpread = Math.max(...positions.map(p => p.x)) - Math.min(...positions.map(p => p.x));
                          const detectedLayout = xSpread > ySpread ? 'horizontal' : 'vertical';
                          
                          updateFieldWithHistory(field.id, {
                            props: {
                              ...field.props,
                              options: finalOptions,
                              layout: detectedLayout
                            }
                          });
                        } else {
                          updateFieldWithHistory(field.id, {
                            props: {
                              ...field.props,
                              options: finalOptions
                            }
                          });
                        }
                      };
                      
                      window.addEventListener('mousemove', handleOptionDrag);
                      window.addEventListener('mouseup', handleOptionDragEnd);
                    }}
                  >
                    {/* Checkbox square */}
                    <rect
                      x={optionX}
                      y={optionY}
                      width={checkboxSize}
                      height={checkboxSize}
                      fill="white"
                      stroke="#9ca3af"
                      strokeWidth="1.5"
                      rx="2"
                      style={{ pointerEvents: 'auto' }}
                    />
                    
                    {/* Check mark placeholder */}
                    <path
                      d={`M ${optionX + 3} ${optionY + 7} L ${optionX + 5.5} ${optionY + 9.5} L ${optionX + 11} ${optionY + 4}`}
                      stroke="#cbd5e0"
                      strokeWidth="1.5"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ pointerEvents: 'auto' }}
                    />
                    
                    {/* Option label - ONLY show if label exists and is not empty */}
                    {option.label && option.label.trim() && (
                      <foreignObject
                        x={optionX + checkboxSize + 6}
                        y={optionY - 2}
                        width={Math.max(0, labelMaxWidth - checkboxSize - 6)}
                        height={optionHeight}
                        style={{ pointerEvents: 'auto' }}
                      >
                        <div
                          xmlns="http://www.w3.org/1999/xhtml"
                          style={{
                            fontSize: '10px',
                            color: '#4b5563',
                            lineHeight: '1.4',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            cursor: 'grab'
                          }}
                        >
                          {option.label}
                        </div>
                      </foreignObject>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })()}
        
        {/* Single Checkbox rendering */}
        {field.type === 'checkbox' && (
          <g pointerEvents="none">
            {/* Checkbox square - centered in field */}
            <rect
              x={pxRect.width / 2 - 10}
              y={pxRect.height / 2 - 10}
              width={20}
              height={20}
              fill="white"
              stroke="#9ca3af"
              strokeWidth="2"
              rx="3"
            />
            {/* Checkmark placeholder */}
            <path
              d={`M ${pxRect.width / 2 - 6} ${pxRect.height / 2} L ${pxRect.width / 2 - 2} ${pxRect.height / 2 + 4} L ${pxRect.width / 2 + 6} ${pxRect.height / 2 - 4}`}
              stroke="#cbd5e0"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )}
        
        {/* Image field specific rendering */}
        {field.type === 'image' && (
          <g pointerEvents="none">
            <foreignObject
              x={pxRect.width / 2 - 20}
              y={pxRect.height / 2 - 20}
              width={40}
              height={40}
            >
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  color: color,
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
              </div>
            </foreignObject>
            <text
              x={pxRect.width / 2}
              y={pxRect.height - 8}
              textAnchor="middle"
              fontSize={10}
              fill="#666"
              pointerEvents="none"
            >
              {field.props?.label || 'Image/Stamp'}
            </text>
          </g>
        )}
        
        {/* Field label (for non-radio, non-checkbox-group, and non-image fields) */}
        {field.type !== 'radio' && field.type !== 'checkbox-group' && field.type !== 'image' && (
          <foreignObject
            x={0}
            y={0}
            width={pxRect.width}
            height={pxRect.height}
            pointerEvents="none"
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px',
                boxSizing: 'border-box'
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  color: field.props?.placeholder ? '#6b7280' : '#333',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  textAlign: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%'
                }}
              >
                {field.props?.placeholder || field.props?.label || field.type}
              </div>
            </div>
          </foreignObject>
        )}
        
        {/* Resize handles (only when selected, not dragging, and resizable) */}
        {isSelected && !isDragging && !isResizing && isResizable && (
          <>
            {/* Corner handles */}
            <circle
              cx={pxRect.width}
              cy={pxRect.height}
              r={6}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'nwse-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 'se')}
              aria-label={`Resize ${field.type} southeast`}
            />
            <circle
              cx={0}
              cy={pxRect.height}
              r={6}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'nesw-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 'sw')}
              aria-label={`Resize ${field.type} southwest`}
            />
            <circle
              cx={pxRect.width}
              cy={0}
              r={6}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'nesw-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 'ne')}
              aria-label={`Resize ${field.type} northeast`}
            />
            <circle
              cx={0}
              cy={0}
              r={6}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'nwse-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 'nw')}
              aria-label={`Resize ${field.type} northwest`}
            />
            
            {/* Edge handles (N, S, E, W) */}
            <circle
              cx={pxRect.width / 2}
              cy={0}
              r={5}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'ns-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 'n')}
              aria-label={`Resize ${field.type} north`}
            />
            <circle
              cx={pxRect.width / 2}
              cy={pxRect.height}
              r={5}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'ns-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 's')}
              aria-label={`Resize ${field.type} south`}
            />
            <circle
              cx={pxRect.width}
              cy={pxRect.height / 2}
              r={5}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'ew-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 'e')}
              aria-label={`Resize ${field.type} east`}
            />
            <circle
              cx={0}
              cy={pxRect.height / 2}
              r={5}
              fill="#fff"
              stroke={color}
              strokeWidth={2}
              style={{ cursor: 'ew-resize' }}
              pointerEvents="auto"
              onMouseDown={(e) => handleMouseDown(e, 'w')}
              aria-label={`Resize ${field.type} west`}
            />
          </>
        )}
      </g>
      
      {/* Add Option Button - OUTSIDE container for Radio and Checkbox Groups */}
      {(field.type === 'radio' || field.type === 'checkbox-group') && isSelected && (
        <g transform={`translate(${pxRect.left}, ${pxRect.top})`}>
          <foreignObject
            x={pxRect.width + 4}
            y={pxRect.height + 4}
            width={32}
            height={32}
            style={{ pointerEvents: 'auto' }}
          >
            <button
              xmlns="http://www.w3.org/1999/xhtml"
              onClick={handleAddOption}
              disabled={(field.props?.options || []).length >= (field.props?.maxOptions || 50)}
              role="button"
              aria-label="Add option"
              aria-disabled={(field.props?.options || []).length >= (field.props?.maxOptions || 50)}
              tabIndex={0}
              title={(field.props?.options || []).length >= (field.props?.maxOptions || 50) ? 'Maximum options reached' : 'Add option'}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: (field.props?.options || []).length >= (field.props?.maxOptions || 50) ? '#d1d5db' : '#6366f1',
                border: '2px solid white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: (field.props?.options || []).length >= (field.props?.maxOptions || 50) ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                transition: 'all 0.12s ease',
                opacity: (field.props?.options || []).length >= (field.props?.maxOptions || 50) ? 0.5 : 1,
                zIndex: 1000
              }}
              onMouseEnter={(e) => {
                if ((field.props?.options || []).length < (field.props?.maxOptions || 50)) {
                  e.target.style.backgroundColor = '#4f46e5';
                  e.target.style.transform = 'scale(1.1)';
                }
              }}
              onMouseLeave={(e) => {
                if ((field.props?.options || []).length < (field.props?.maxOptions || 50)) {
                  e.target.style.backgroundColor = '#6366f1';
                  e.target.style.transform = 'scale(1)';
                }
              }}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && (field.props?.options || []).length < (field.props?.maxOptions || 50)) {
                  e.preventDefault();
                  handleAddOption(e);
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
          </foreignObject>
        </g>
      )}
    </>
  );
};

export default FieldNode;
